import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { 
  DollarSign, Save, Loader2, RefreshCw, Tag, Check, X, 
  TrendingUp, Building2, ChevronDown, ChevronUp 
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const getAuthHeaders = () => ({
  'Authorization': `Bearer ${localStorage.getItem('session_token')}`
});

const ProfessionPricing = () => {
  const [professions, setProfessions] = useState([]);
  const [templates, setTemplates] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(null);
  const [expandedProfession, setExpandedProfession] = useState(null);
  const [editingPrices, setEditingPrices] = useState({});

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [pricingRes, templatesRes] = await Promise.all([
        axios.get(`${API}/pricing/all`, { headers: getAuthHeaders() }),
        axios.get(`${API}/pricing/templates`)
      ]);
      
      setProfessions(pricingRes.data.professions || []);
      setTemplates(templatesRes.data.templates || {});
    } catch (error) {
      console.error('Error loading pricing data:', error);
      toast.error('Error al cargar los precios');
    } finally {
      setLoading(false);
    }
  };

  const handlePriceChange = (professionId, field, value) => {
    setEditingPrices(prev => ({
      ...prev,
      [professionId]: {
        ...prev[professionId],
        [field]: parseFloat(value) || 0
      }
    }));
  };

  const saveProfessionPricing = async (professionId) => {
    const changes = editingPrices[professionId];
    if (!changes || Object.keys(changes).length === 0) {
      toast.info('No hay cambios que guardar');
      return;
    }

    try {
      setSaving(professionId);
      await axios.put(
        `${API}/pricing/profession/${professionId}`,
        changes,
        { headers: getAuthHeaders() }
      );
      toast.success('Precios actualizados correctamente');
      setEditingPrices(prev => {
        const newState = { ...prev };
        delete newState[professionId];
        return newState;
      });
      loadData();
    } catch (error) {
      console.error('Error saving pricing:', error);
      toast.error('Error al guardar los precios');
    } finally {
      setSaving(null);
    }
  };

  const resetToDefault = async (professionId) => {
    if (!window.confirm('Esto restaurara los precios a los valores por defecto de la categoria. Continuar?')) {
      return;
    }

    try {
      setSaving(professionId);
      await axios.post(
        `${API}/pricing/profession/${professionId}/reset`,
        {},
        { headers: getAuthHeaders() }
      );
      toast.success('Precios reseteados a valores por defecto');
      setEditingPrices(prev => {
        const newState = { ...prev };
        delete newState[professionId];
        return newState;
      });
      loadData();
    } catch (error) {
      console.error('Error resetting pricing:', error);
      toast.error('Error al resetear los precios');
    } finally {
      setSaving(null);
    }
  };

  const getCategoryColor = (category) => {
    const colors = {
      sanidad: 'bg-green-100 text-green-800',
      seguridad: 'bg-blue-100 text-blue-800',
      justicia: 'bg-purple-100 text-purple-800',
      educacion: 'bg-yellow-100 text-yellow-800',
      administracion: 'bg-gray-100 text-gray-800',
      default: 'bg-slate-100 text-slate-800'
    };
    return colors[category] || colors.default;
  };

  const getCurrentValue = (profession, field) => {
    if (editingPrices[profession.profession_id]?.[field] !== undefined) {
      return editingPrices[profession.profession_id][field];
    }
    return profession[field] || 0;
  };

  const hasChanges = (professionId) => {
    return editingPrices[professionId] && Object.keys(editingPrices[professionId]).length > 0;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
        <span className="ml-2 text-slate-600">Cargando precios...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-slate-800">Precios B2C por Profesion</h2>
          <p className="text-slate-600">Configura los precios de los planes para cada profesion</p>
        </div>
        <Button onClick={loadData} variant="outline" className="gap-2">
          <RefreshCw className="w-4 h-4" />
          Actualizar
        </Button>
      </div>

      {/* Templates de referencia */}
      <div className="bg-gradient-to-r from-indigo-50 to-purple-50 rounded-lg p-4 border border-indigo-200">
        <h3 className="font-semibold text-indigo-800 mb-3 flex items-center gap-2">
          <Tag className="w-4 h-4" />
          Plantillas de Precios por Categoria
        </h3>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
          {Object.entries(templates).map(([category, prices]) => (
            <div key={category} className="bg-white rounded-lg p-3 shadow-sm">
              <span className={`inline-block px-2 py-1 rounded-full text-xs font-medium mb-2 ${getCategoryColor(category)}`}>
                {category.charAt(0).toUpperCase() + category.slice(1)}
              </span>
              <div className="text-xs text-slate-600 space-y-1">
                <p>Basico: {prices.monthly_basic}EUR/mes</p>
                <p>Premium: {prices.monthly_premium}EUR/mes</p>
                <p>Pro: {prices.monthly_pro}EUR/mes</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Lista de profesiones */}
      <div className="space-y-4">
        {professions.length === 0 ? (
          <div className="text-center py-12 bg-slate-50 rounded-lg">
            <Building2 className="w-12 h-12 mx-auto text-slate-400 mb-3" />
            <p className="text-slate-600">No hay profesiones configuradas</p>
          </div>
        ) : (
          professions.map((profession) => (
            <div 
              key={profession.profession_id}
              className={`bg-white rounded-lg border shadow-sm transition-all ${
                hasChanges(profession.profession_id) ? 'border-amber-400 ring-1 ring-amber-200' : 'border-slate-200'
              }`}
            >
              {/* Header */}
              <div 
                className="flex items-center justify-between p-4 cursor-pointer hover:bg-slate-50"
                onClick={() => setExpandedProfession(
                  expandedProfession === profession.profession_id ? null : profession.profession_id
                )}
              >
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-indigo-100 rounded-lg">
                    <DollarSign className="w-5 h-5 text-indigo-600" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-slate-800">{profession.name}</h4>
                    <div className="flex items-center gap-2 mt-1">
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${getCategoryColor(profession.category)}`}>
                        {profession.category}
                      </span>
                      {profession.is_custom && (
                        <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800">
                          Personalizado
                        </span>
                      )}
                      {hasChanges(profession.profession_id) && (
                        <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800 animate-pulse">
                          Sin guardar
                        </span>
                      )}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <div className="text-right text-sm text-slate-600">
                    <p>Basico: <span className="font-semibold">{profession.monthly_basic}EUR</span></p>
                    <p>Premium: <span className="font-semibold">{profession.monthly_premium}EUR</span></p>
                  </div>
                  {expandedProfession === profession.profession_id ? (
                    <ChevronUp className="w-5 h-5 text-slate-400" />
                  ) : (
                    <ChevronDown className="w-5 h-5 text-slate-400" />
                  )}
                </div>
              </div>

              {/* Expanded content */}
              {expandedProfession === profession.profession_id && (
                <div className="border-t p-4 bg-slate-50">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    {/* Plan Basico */}
                    <div className="bg-white rounded-lg p-4 border border-slate-200">
                      <h5 className="font-semibold text-slate-700 mb-3 flex items-center gap-2">
                        <div className="w-2 h-2 bg-slate-400 rounded-full" />
                        Plan Basico
                      </h5>
                      <div className="space-y-3">
                        <div>
                          <label className="text-xs text-slate-500">Precio Mensual</label>
                          <div className="relative">
                            <Input
                              type="number"
                              step="0.01"
                              value={getCurrentValue(profession, 'monthly_basic')}
                              onChange={(e) => handlePriceChange(profession.profession_id, 'monthly_basic', e.target.value)}
                              className="pr-10"
                            />
                            <span className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400">EUR</span>
                          </div>
                        </div>
                        <div>
                          <label className="text-xs text-slate-500">Precio Anual</label>
                          <div className="relative">
                            <Input
                              type="number"
                              step="0.01"
                              value={getCurrentValue(profession, 'annual_basic')}
                              onChange={(e) => handlePriceChange(profession.profession_id, 'annual_basic', e.target.value)}
                              className="pr-10"
                            />
                            <span className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400">EUR</span>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Plan Premium */}
                    <div className="bg-white rounded-lg p-4 border-2 border-indigo-200">
                      <h5 className="font-semibold text-indigo-700 mb-3 flex items-center gap-2">
                        <div className="w-2 h-2 bg-indigo-500 rounded-full" />
                        Plan Premium
                        <span className="ml-auto text-xs bg-indigo-100 text-indigo-600 px-2 py-0.5 rounded-full">Popular</span>
                      </h5>
                      <div className="space-y-3">
                        <div>
                          <label className="text-xs text-slate-500">Precio Mensual</label>
                          <div className="relative">
                            <Input
                              type="number"
                              step="0.01"
                              value={getCurrentValue(profession, 'monthly_premium')}
                              onChange={(e) => handlePriceChange(profession.profession_id, 'monthly_premium', e.target.value)}
                              className="pr-10"
                            />
                            <span className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400">EUR</span>
                          </div>
                        </div>
                        <div>
                          <label className="text-xs text-slate-500">Precio Anual</label>
                          <div className="relative">
                            <Input
                              type="number"
                              step="0.01"
                              value={getCurrentValue(profession, 'annual_premium')}
                              onChange={(e) => handlePriceChange(profession.profession_id, 'annual_premium', e.target.value)}
                              className="pr-10"
                            />
                            <span className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400">EUR</span>
                          </div>
                        </div>
                        <div>
                          <label className="text-xs text-slate-500">Creditos IA incluidos/mes</label>
                          <Input
                            type="number"
                            value={getCurrentValue(profession, 'credits_premium') || 200}
                            onChange={(e) => handlePriceChange(profession.profession_id, 'credits_premium', e.target.value)}
                          />
                        </div>
                      </div>
                    </div>

                    {/* Plan Pro */}
                    <div className="bg-white rounded-lg p-4 border-2 border-purple-200">
                      <h5 className="font-semibold text-purple-700 mb-3 flex items-center gap-2">
                        <div className="w-2 h-2 bg-purple-500 rounded-full" />
                        Plan Pro
                        <TrendingUp className="w-4 h-4 ml-auto text-purple-500" />
                      </h5>
                      <div className="space-y-3">
                        <div>
                          <label className="text-xs text-slate-500">Precio Mensual</label>
                          <div className="relative">
                            <Input
                              type="number"
                              step="0.01"
                              value={getCurrentValue(profession, 'monthly_pro')}
                              onChange={(e) => handlePriceChange(profession.profession_id, 'monthly_pro', e.target.value)}
                              className="pr-10"
                            />
                            <span className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400">EUR</span>
                          </div>
                        </div>
                        <div>
                          <label className="text-xs text-slate-500">Precio Anual</label>
                          <div className="relative">
                            <Input
                              type="number"
                              step="0.01"
                              value={getCurrentValue(profession, 'annual_pro')}
                              onChange={(e) => handlePriceChange(profession.profession_id, 'annual_pro', e.target.value)}
                              className="pr-10"
                            />
                            <span className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400">EUR</span>
                          </div>
                        </div>
                        <div>
                          <label className="text-xs text-slate-500">Creditos IA incluidos/mes</label>
                          <Input
                            type="number"
                            value={getCurrentValue(profession, 'credits_pro') || 500}
                            onChange={(e) => handlePriceChange(profession.profession_id, 'credits_pro', e.target.value)}
                          />
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Trial config */}
                  <div className="mt-4 p-4 bg-white rounded-lg border border-slate-200">
                    <h5 className="font-semibold text-slate-700 mb-3">Configuracion de Prueba Gratuita</h5>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="text-xs text-slate-500">Dias de prueba</label>
                        <Input
                          type="number"
                          value={getCurrentValue(profession, 'trial_days') || 7}
                          onChange={(e) => handlePriceChange(profession.profession_id, 'trial_days', e.target.value)}
                        />
                      </div>
                      <div>
                        <label className="text-xs text-slate-500">Creditos IA en prueba</label>
                        <Input
                          type="number"
                          value={getCurrentValue(profession, 'trial_credits') || 50}
                          onChange={(e) => handlePriceChange(profession.profession_id, 'trial_credits', e.target.value)}
                        />
                      </div>
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex justify-end gap-3 mt-4">
                    <Button
                      variant="outline"
                      onClick={() => resetToDefault(profession.profession_id)}
                      disabled={saving === profession.profession_id}
                      className="gap-2"
                    >
                      <RefreshCw className="w-4 h-4" />
                      Resetear a Defecto
                    </Button>
                    <Button
                      onClick={() => saveProfessionPricing(profession.profession_id)}
                      disabled={saving === profession.profession_id || !hasChanges(profession.profession_id)}
                      className="gap-2 bg-indigo-600 hover:bg-indigo-700"
                    >
                      {saving === profession.profession_id ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <Save className="w-4 h-4" />
                      )}
                      Guardar Cambios
                    </Button>
                  </div>
                </div>
              )}
            </div>
          ))
        )}
      </div>

      {/* Nota informativa */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <h4 className="font-semibold text-blue-800 mb-2">Información</h4>
        <ul className="text-sm text-blue-700 space-y-1">
          <li>• Los precios se aplican automáticamente según la categoría de la profesión</li>
          <li>• Puedes personalizar los precios para cada profesión individualmente</li>
          <li>• Los cambios afectarán a las nuevas suscripciones, no a las existentes</li>
          <li>• El precio anual debería ofrecer un descuento (típicamente 2 meses gratis)</li>
        </ul>
      </div>

      {/* Margin Analysis */}
      <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
        <h4 className="font-semibold text-amber-800 mb-2 flex items-center gap-2">
          <TrendingUp className="w-5 h-5" />
          Análisis de Márgenes (Objetivo: &gt;60%)
        </h4>
        <p className="text-sm text-amber-700 mb-3">
          Coste estimado por usuario/mes: ~3€ (hosting) + créditos IA (~0.005€/crédito)
        </p>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-amber-200">
                <th className="text-left py-2 text-amber-800">Plan</th>
                <th className="text-center py-2 text-amber-800">Precio</th>
                <th className="text-center py-2 text-amber-800">Coste Est.</th>
                <th className="text-center py-2 text-amber-800">Margen</th>
                <th className="text-center py-2 text-amber-800">Estado</th>
              </tr>
            </thead>
            <tbody>
              {professions.slice(0, 5).map(p => {
                const costs = {
                  basic: 3, // Sin IA, solo hosting
                  premium: 3 + (p.credits_premium || 200) * 0.005, // + créditos IA
                  pro: 3 + (p.credits_pro || 500) * 0.005 // + créditos IA voz
                };
                const margins = {
                  basic: ((p.monthly_basic - costs.basic) / p.monthly_basic * 100).toFixed(0),
                  premium: ((p.monthly_premium - costs.premium) / p.monthly_premium * 100).toFixed(0),
                  pro: ((p.monthly_pro - costs.pro) / p.monthly_pro * 100).toFixed(0)
                };
                return (
                  <tr key={p.profession_id} className="border-b border-amber-100">
                    <td className="py-2 font-medium text-slate-700">{p.name?.substring(0, 25)}...</td>
                    <td className="text-center py-2">
                      <div className="text-xs space-y-0.5">
                        <div>B: {p.monthly_basic}€</div>
                        <div>P: {p.monthly_premium}€</div>
                        <div>Pro: {p.monthly_pro}€</div>
                      </div>
                    </td>
                    <td className="text-center py-2">
                      <div className="text-xs space-y-0.5">
                        <div>{costs.basic.toFixed(1)}€</div>
                        <div>{costs.premium.toFixed(1)}€</div>
                        <div>{costs.pro.toFixed(1)}€</div>
                      </div>
                    </td>
                    <td className="text-center py-2">
                      <div className="text-xs space-y-0.5">
                        <div className={margins.basic >= 60 ? 'text-green-600' : 'text-red-600'}>{margins.basic}%</div>
                        <div className={margins.premium >= 60 ? 'text-green-600' : 'text-red-600'}>{margins.premium}%</div>
                        <div className={margins.pro >= 60 ? 'text-green-600' : 'text-red-600'}>{margins.pro}%</div>
                      </div>
                    </td>
                    <td className="text-center py-2">
                      {margins.basic >= 60 && margins.premium >= 60 && margins.pro >= 60 ? (
                        <span className="text-green-600">✓ OK</span>
                      ) : (
                        <span className="text-red-600">⚠️ Revisar</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default ProfessionPricing;
